import React, { useState, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Box,
  Typography,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Paper,
} from "@mui/material";
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  Delete as DeleteIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import enoviaService from "../api/Services/enoviaService";
import { clearDeliverablesCache } from "./DeliverablesDots";
import { updateTeamTask } from "../utility/teamProjectService";

const DeliverableModal = ({ task, onClose }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({}); // { fileName: 'pending' | 'uploading' | 'success' | 'error' }

  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles((prev) => [...prev, ...droppedFiles]);
  }, []);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (files.length === 0) return;
    setUploading(true);

    const service = new enoviaService();
    let successCount = 0;

    for (const file of files) {
      if (uploadStatus[file.name] === "success") {
        successCount++;
        continue;
      }

      setUploadStatus((prev) => ({ ...prev, [file.name]: "uploading" }));

      try {
        // Step 1: Get Checkin Ticket
        const ticketRes = await service.getCheckinTicket();
        if (ticketRes.status !== 200 && ticketRes.status !== 201) throw new Error("Failed to get checkin ticket");
        const ticketData = ticketRes.data || ticketRes;

        // Extract ticket URL and job ticket (handling different possible 3DSpace payload formats)
        const elements = ticketData.data?.[0]?.dataelements || ticketData;
        const ticketUrl = elements.ticketURL || elements.ticketurl || elements.url;
        const jobTicket = elements.jobTicket || elements.__fcs__jobTicket || elements.ticket;

        if (!ticketUrl || !jobTicket) {
          throw new Error("Could not parse FCS ticket from response");
        }

        // Step 2: Upload physical file to FCS
        const receipt = await service.uploadToFCS(ticketUrl, jobTicket, file);

        // Step 3: Create Document Object in 3DSpace
        // The receipt must go in relateddata.files — NOT in dataelements.
        // Putting it in dataelements creates the metadata record but drops the file.
        const docPayload = {
          data: [
            {
              type: "Document",
              dataelements: {
                title: file.name,
                description: "Uploaded via Pert Chart Widget",
              },
              relateddata: {
                files: [
                  {
                    dataelements: {
                      title: file.name,
                      receipt: receipt,
                      direction: "output",
                    },
                  },
                ],
              },
            },
          ],
        };

        const docRes = await service.createDocument(docPayload);
        if (docRes.status !== 200 && docRes.status !== 201) throw new Error("Failed to create Document");
        const docId = docRes.data?.data?.[0]?.id;

        if (!docId) throw new Error("No Document ID returned after creation");

        // Step 3b: Checkin the file to the document to finalise the physical file attachment
        // This step is required to commit the FCS receipt to the specific document object.
        try {
          await service.checkinDocumentFile(docId, {
            data: [
              {
                id: docId,
                dataelements: {
                  receipt: receipt,
                },
              },
            ],
          });
        } catch (checkinErr) {
          // Checkin step may not always be required depending on the 3DSpace config.
          // Log but don't fail — document creation may already have attached the file via relateddata.
          console.warn("[Deliverables] Checkin step failed (may be OK):", checkinErr);
        }

        const isTeamProj = task.id && (task.id.startsWith("uuid:") || task.id.includes("-"));

        if (isTeamProj) {
          await updateTeamTask(task.id, {
            hasDeliverable: [
              {
                operation: "add",
                value: {
                  identifier: docId,
                  service: "3DSpace"
                }
              }
            ]
          });
          setUploadStatus((prev) => ({ ...prev, [file.name]: "success" }));
          successCount++;
        } else {
          // Step 4: Attach the newly created Document to the Task
          const attachPayload = {
            data: [
              {
                id: docId,
                type: "Document"
              }
            ]
          };

          const attachRes = await service.addDeliverables(task.id, attachPayload);

          if (attachRes.status === 200 || attachRes.status === 201) {
            setUploadStatus((prev) => ({ ...prev, [file.name]: "success" }));
            successCount++;
          } else {
            throw new Error("Upload failed to attach to task");
          }
        }
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        setUploadStatus((prev) => ({ ...prev, [file.name]: "error" }));
      }
    }

    setUploading(false);

    const failCount = files.length - successCount;

    if (successCount === files.length) {
      toast.success("All deliverables uploaded successfully!");
      clearDeliverablesCache(task.id); // clears cache + fires refreshDeliverables event
      setTimeout(() => {
        onClose();
      }, 1500);
    } else if (successCount > 0) {
      toast.warning(`${successCount} file(s) uploaded successfully, but ${failCount} failed.`);
      clearDeliverablesCache(task.id); // clears cache + fires refreshDeliverables event
    } else {
      toast.error("Failed to upload deliverables. Please check connection and try again.");
    }
  };

  return createPortal(
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000000000,
        backdropFilter: "blur(4px)",
      }}
      onClick={onClose}
    >
      <Paper
        elevation={24}
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: 500,
          maxHeight: "80vh",
          backgroundColor: "#fff",
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          animation: "modalFadeIn 0.3s ease-out",
          "@keyframes modalFadeIn": {
            from: { opacity: 0, transform: "scale(0.95)" },
            to: { opacity: 1, transform: "scale(1)" },
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid #d1d4d4",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#005686", // DS 3DEXPERIENCE Topbar Color
            color: "white",
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
              Add Deliverables
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {task.title}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="medium"
            sx={{
              color: "white",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                transform: "rotate(90deg)"
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ p: 3, flex: 1, overflowY: "auto" }}>
          {/* Drop Zone */}
          <Box
            onDrop={handleFileDrop}
            onDragOver={(e) => e.preventDefault()}
            sx={{
              border: "2px dashed #005686",
              borderRadius: "8px",
              p: 4,
              textAlign: "center",
              backgroundColor: "rgba(0, 86, 134, 0.03)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "rgba(0, 86, 134, 0.08)",
                borderColor: "#00446b",
              },
            }}
            onClick={() => document.getElementById("file-input").click()}
          >
            <input
              id="file-input"
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />
            <UploadIcon sx={{ fontSize: 48, color: "#005686", mb: 1 }} />
            <Typography variant="body1" sx={{ fontWeight: 500, color: "#333" }}>
              Drag & Drop files here
            </Typography>
            <Typography variant="body2" sx={{ color: "#666" }}>
              or click to browse
            </Typography>
          </Box>

          {/* File List */}
          {files.length > 0 && (
            <List sx={{ mt: 2 }}>
              {files.map((file, index) => (
                <ListItem
                  key={index}
                  sx={{
                    border: "1px solid #eee",
                    borderRadius: "6px",
                    mb: 1,
                    backgroundColor: "#fcfcfc",
                  }}
                >
                  <ListItemIcon>
                    <FileIcon sx={{ color: "#5f6368" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={file.name}
                    secondary={`${(file.size / 1024).toFixed(1)} KB`}
                    primaryTypographyProps={{
                      noWrap: true,
                      variant: "body2",
                      fontWeight: 500,
                    }}
                  />
                  <ListItemSecondaryAction>
                    {uploadStatus[file.name] === "uploading" ? (
                      <CircularProgress size={20} thickness={5} />
                    ) : uploadStatus[file.name] === "success" ? (
                      <SuccessIcon sx={{ color: "#4caf50" }} />
                    ) : uploadStatus[file.name] === "error" ? (
                      <ErrorIcon sx={{ color: "#f44336" }} />
                    ) : (
                      <IconButton
                        edge="end"
                        onClick={() => removeFile(index)}
                        disabled={uploading}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid #eee",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            backgroundColor: "#f8f9fa",
          }}
        >
          {(() => {
            const hasSucceeded = Object.values(uploadStatus).some(v => v === 'success');
            return (
              <Button
                onClick={onClose}
                disabled={uploading}
                variant={hasSucceeded ? "contained" : "text"}
                sx={{
                  color: hasSucceeded ? "white" : "#5f6368",
                  textTransform: "none",
                  backgroundColor: hasSucceeded ? "#005686" : "transparent",
                  "&:hover": {
                    backgroundColor: hasSucceeded ? "#00446b" : "rgba(0,0,0,0.04)"
                  }
                }}
              >
                {hasSucceeded ? "Close" : "Cancel"}
              </Button>
            );
          })()}
          <Button
            variant="contained"
            onClick={uploadFiles}
            disabled={files.length === 0 || uploading}
            startIcon={
              uploading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />
            }
            sx={{
              backgroundColor: "#005686",
              textTransform: "none",
              px: 3,
              "&:hover": { backgroundColor: "#00446b" },
            }}
          >
            {uploading ? "Uploading..." : "Upload Deliverables"}
          </Button>
        </Box>
      </Paper>
    </Box>,
    document.body
  );
};

export default DeliverableModal;
